import { Injectable, RendererFactory2, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CUSTOMAPIService } from 'app/custom-api.service';

export type AppTheme = 'dark-theme' | 'light-theme';

/** localStorage key for the fluid-typography feature flag (OFF by default). */
const FLUID_TYPE_STORAGE_KEY = 'fluidType';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private themeSubject = new BehaviorSubject<AppTheme>('dark-theme');
  readonly currentTheme$ = this.themeSubject.asObservable();

  /** Whether the current route is under /collection */
  private onCollectionRoute = new BehaviorSubject<boolean>(false);
  readonly isOnCollectionRoute$ = this.onCollectionRoute.asObservable();

  /** Whether the theme toggle should be disabled (ai-tasks, ai-review, ai-wizard) */
  private themeToggleDisabled = new BehaviorSubject<boolean>(false);
  readonly isThemeToggleDisabled$ = this.themeToggleDisabled.asObservable();

  /** Persisted user preference */
  private userId: string | null = null;
  private userPreference = false; // true = light theme

  /** Theme active just before entering an AI page (ai-tasks/ai-review), restored on exit. */
  private themeBeforeAiPage: AppTheme | null = null;

  constructor(
    private rendererFactory: RendererFactory2,
    private router: Router,
    private customApi: CUSTOMAPIService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.applyTheme(this.themeSubject.value);
    this.applyFluidType(this.fluidTypeEnabled);

    // Watch route changes — auto-apply/revert theme based on route and user preference
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url || '';
      const isCollection = url.includes('/collection') && url.includes('/first-party/');
      const isAiPage = isCollection && (url.includes('/ai-tasks') || url.includes('/ai-review'));
      this.onCollectionRoute.next(isCollection);
      this.themeToggleDisabled.next(isAiPage);

      if (isAiPage) {
        // AI Tasks / AI Review are always dark, regardless of the user's light-mode preference.
        if (this.themeBeforeAiPage === null) {
          this.themeBeforeAiPage = this.themeSubject.value;
        }
        if (this.isLightMode) {
          this.setTheme('dark-theme');
        }
        return;
      }

      if (this.themeBeforeAiPage !== null) {
        // Leaving an AI page — restore whatever theme was active beforehand.
        const restoreTheme = this.themeBeforeAiPage;
        this.themeBeforeAiPage = null;
        if (restoreTheme !== this.themeSubject.value) {
          this.setTheme(restoreTheme);
          return;
        }
      }

      if (isCollection && this.userPreference && this.isDarkMode) {
        this.setTheme('light-theme');
      } else if (!isCollection && this.isLightMode) {
        this.setTheme('dark-theme');
      }
    });
  }

  /** Initialize theme preference from the logged-in user object. Call once after login. */
  initializeFromUser(user: { id: string; lightTheme?: boolean | null }): void {
    if (!user || this.userId === user.id) {
      return;
    }
    this.userId = user.id;
    this.userPreference = !!user.lightTheme;
    // If currently on a collection route and user prefers light, apply immediately
    if (this.onCollectionRoute.value && this.userPreference) {
      this.setTheme('light-theme');
    }
  }

  /** Persist current theme choice to the backend */
  async saveThemePreference(): Promise<void> {
    if (!this.userId) {
      return;
    }
    this.userPreference = this.isLightMode;
    try {
      await this.customApi.UpdateUser({
        id: this.userId,
        lightTheme: this.isLightMode,
      });
    } catch (e) {
      console.error('Failed to save theme preference', e);
    }
  }

  get currentTheme(): AppTheme {
    return this.themeSubject.value;
  }

  get isDarkMode(): boolean {
    return this.themeSubject.value === 'dark-theme';
  }

  get isLightMode(): boolean {
    return this.themeSubject.value === 'light-theme';
  }

  toggleTheme(): void {
    const newTheme: AppTheme = this.isDarkMode ? 'light-theme' : 'dark-theme';
    this.setTheme(newTheme);
  }

  /**
   * Fluid-typography feature flag (OFF by default). When enabled, the
   * --font-* tokens in styles.scss switch from fixed canonical sizes to
   * fluid clamp() values via the body.fluid-type class.
   */
  get fluidTypeEnabled(): boolean {
    try {
      return localStorage.getItem(FLUID_TYPE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  setFluidType(enabled: boolean): void {
    try {
      localStorage.setItem(FLUID_TYPE_STORAGE_KEY, String(enabled));
    } catch {
      // storage unavailable — apply for this session only
    }
    this.applyFluidType(enabled);
  }

  private applyFluidType(enabled: boolean): void {
    if (enabled) {
      this.renderer.addClass(document.body, 'fluid-type');
    } else {
      this.renderer.removeClass(document.body, 'fluid-type');
    }
  }

  /** Manually override the disabled state — used by modals like the AI Wizard */
  setThemeToggleDisabled(disabled: boolean): void {
    this.themeToggleDisabled.next(disabled);
  }

  setTheme(theme: AppTheme): void {
    this.applyTheme(theme);
    this.themeSubject.next(theme);
  }

  getCssVar(varName: string): string {
    return getComputedStyle(document.body).getPropertyValue(varName).trim();
  }

  private applyTheme(theme: AppTheme): void {
    const body = document.body;
    this.renderer.removeClass(body, 'dark-theme');
    this.renderer.removeClass(body, 'light-theme');
    this.renderer.addClass(body, theme);
  }
}
